import { Quote, ShieldHalf, GraduationCap, Instagram, Facebook } from "lucide-react";

export default function Leadership() {
  return (
    <section
      id="leadership"
      data-testid="leadership-section"
      className="relative bg-[#08080A] py-32 md:py-48"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-20">
          <div className="md:col-span-8">
            <div className="overline mb-6">Leadership</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-neutral-50">
              A house is only <br />
              as <span className="italic text-[#D4AF37]">honest</span> as its hand.
            </h2>
          </div>
        </div>

        {/* ============ Dr. Anurag Singh ============ */}
        <div
          data-testid="leader-anurag"
          className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-start mb-28"
        >
          <div className="md:col-span-5">
            <div className="relative aspect-[4/5] overflow-hidden border border-white/8">
              <img
                src="https://customer-assets-v7afamib.emergentagent.net/job_rudra-realestate/artifacts/ku1qtp6i_Business%20profile%20picture..png"
                alt="Dr. Anurag Singh"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#08080A]/80 via-transparent to-transparent" />
            </div>
            <div className="mt-6 flex flex-wrap gap-2">
              {["MBBS", "MS · General Surgery", "M.Ch · CTVS"].map((c) => (
                <span
                  key={c}
                  className="px-3 py-1.5 border border-[#D4AF37]/40 text-[0.65rem] tracking-widest uppercase text-[#D4AF37]"
                >
                  {c}
                </span>
              ))}
            </div>
          </div>

          <div className="md:col-span-7 md:pt-6">
            <div className="overline mb-4 text-neutral-500">
              Chairman &amp; Legal Owner, Rudra Empire
            </div>
            <div className="font-display text-5xl md:text-6xl text-neutral-50 leading-none">
              Dr. Anurag Singh
            </div>
            <div className="mt-3 text-neutral-400 text-sm tracking-wide">
              MBBS, MS (General Surgery), M.Ch (Cardiothoracic &amp; Vascular Surgery)
            </div>

            <div className="mt-10 space-y-6 text-neutral-300 leading-relaxed max-w-2xl text-base md:text-lg">
              <p>
                Dr. Anurag Singh assumed legal ownership of RUDRA EMPIRE in
                2011, at the age of eighteen, following the passing of the
                founder. He serves as the firm&apos;s Chairman and Legal Owner.
              </p>
              <p>
                To ensure operational continuity while he completed his medical
                education, he entrusted the{" "}
                <span className="text-neutral-50">Power of Attorney (POA)</span>{" "}
                for day-to-day management to the founder&apos;s long-standing
                Manager and Trustee,{" "}
                <span className="text-neutral-50">Mr. Jasprit Singh</span>. All
                material decisions remain under Dr. Anurag Singh&apos;s
                strategic oversight.
              </p>
              <p>
                Dr. Anurag Singh is a qualified surgeon holding an{" "}
                <span className="text-neutral-50">
                  MBBS, MS in General Surgery, and M.Ch in Cardiothoracic
                  &amp; Vascular Surgery
                </span>
                . The discipline, precision and diligence of medical practice
                inform the standards he brings to the stewardship of RUDRA
                EMPIRE.
              </p>
              <p>
                Having completed his medical training, Dr. Anurag Singh now
                devotes himself full-time to the leadership of RUDRA EMPIRE —
                guiding the firm&apos;s strategic direction as a private house
                of land for Government bodies and corporate counterparties in
                Karnataka.
              </p>

              {/* ── Vision ── */}
              <div className="pt-6 border-t border-white/8">
                <div className="overline mb-4">The Vision</div>
                <p>
                  Under his direction, RUDRA EMPIRE is being quietly
                  re-engineered for the next chapter of India&apos;s build-out.
                  Dr. Anurag has articulated a clear intent for the house — to
                  become the private land partner of choice for the sovereign
                  and industrial infrastructure that will define India&apos;s
                  next two decades:{" "}
                  <span className="text-neutral-50">
                    semiconductor fabrication clusters, gigawatt data centres,
                    defence corridors, greenfield gigafactories, and
                    high-speed rail alignments
                  </span>. Where the ambition of a Government department or a
                  multinational meets the ground of Karnataka, he intends the
                  House of Rudra to be the first name that comes to mind.
                </p>
              </div>

              {/* ── Signature quote ── */}
              <div className="mt-6 border-l-2 border-[#D4AF37] pl-6 py-2">
                <Quote strokeWidth={1} className="w-6 h-6 text-[#D4AF37] mb-3" />
                <p className="font-display italic text-xl md:text-2xl text-neutral-100 leading-snug">
                  “My father chose land because he understood, ahead of his
                  time, that the nation which would build the twenty-first
                  century would first have to lawfully own the ground beneath
                  it. My work is to carry that discipline forward — private,
                  discreet, modernised — into an era where India needs
                  thousands of contiguous acres, delivered on institutional
                  timelines. That, to me, is the mandate.”
                </p>
                <div className="mt-3 overline">— Dr. Anurag Singh</div>
              </div>

              {/* ── Social — Anurag personal ── */}
              <div className="pt-6 flex items-center gap-4">
                <div className="overline text-neutral-500 mr-2">Follow the Chairman</div>
                <a
                  data-testid="anurag-social-x"
                  href="https://x.com/TheAnurag__"
                  target="_blank"
                  rel="noreferrer noopener"
                  aria-label="Dr. Anurag Singh on X"
                  className="w-9 h-9 border border-white/15 hover:border-[#D4AF37] hover:text-[#D4AF37] text-neutral-300 flex items-center justify-center transition-colors"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                </a>
                <a
                  data-testid="anurag-social-instagram"
                  href="https://www.instagram.com/theanurag_11/"
                  target="_blank"
                  rel="noreferrer noopener"
                  aria-label="Dr. Anurag Singh on Instagram"
                  className="w-9 h-9 border border-white/15 hover:border-[#D4AF37] hover:text-[#D4AF37] text-neutral-300 flex items-center justify-center transition-colors"
                >
                  <Instagram strokeWidth={1.4} className="w-4 h-4" />
                </a>
                <a
                  data-testid="anurag-social-facebook"
                  href="https://www.facebook.com/TheAnuraag11"
                  target="_blank"
                  rel="noreferrer noopener"
                  aria-label="Dr. Anurag Singh on Facebook"
                  className="w-9 h-9 border border-white/15 hover:border-[#D4AF37] hover:text-[#D4AF37] text-neutral-300 flex items-center justify-center transition-colors"
                >
                  <Facebook strokeWidth={1.4} className="w-4 h-4" />
                </a>
                <span className="text-[0.6rem] tracking-widest uppercase text-neutral-500 ml-2">Personal profiles of the Chairman</span>
              </div>
            </div>

            <div className="mt-10 grid grid-cols-2 gap-4 max-w-md">
              {[
                { i: GraduationCap, l: "MBBS · MS · M.Ch" },
                { i: ShieldHalf, l: "Chairman · Since 2011" },
              ].map(({ i: Icon, l }) => (
                <div key={l} className="border border-white/8 p-4">
                  <Icon strokeWidth={1} className="w-5 h-5 text-[#D4AF37] mb-2" />
                  <div className="text-[0.68rem] tracking-widest uppercase text-neutral-400">
                    {l}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Founder + Trustee blocks moved to /house-of-rudra page */}
      </div>
    </section>
  );
}
